// Wealth distribution donut — neumorphic 3D style matching the commitments dial.
import { useMemo } from "react";
import { Landmark, Wallet, Coins, PiggyBank } from "lucide-react";
import { useAccounts } from "@/lib/useAccounts";
import { useAuth } from "@/context/AuthContext";
import { useCurrencyTotal, convertAmount } from "@/lib/useCurrencyTotal";
import { useI18n } from "@/context/I18nContext";

const COLORS = ["#1e3a8a", "#3b82f6", "#f5b800", "#10b981", "#ef4444", "#8b5cf6", "#f97316", "#06b6d4"];
const ICON = { bank: Landmark, cash: Coins, wallet: Wallet, savings: PiggyBank };
const R = 82, STROKE = 26, C = 2 * Math.PI * R;

export function NeuAccountsDonut() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { items = [] } = useAccounts();
  const home = (user?.currency || "AED").toUpperCase();
  const entries = items.map((a) => ({ amount: Number(a.balance ?? a.opening_balance ?? 0), currency: a.currency }));
  const { rates } = useCurrencyTotal(entries, home);

  const data = useMemo(() => {
    const conv = items.map((a) => ({
      a, value: convertAmount(Number(a.balance ?? a.opening_balance ?? 0), a.currency, home, rates) || 0,
    })).filter((x) => x.value > 0);
    const totalVal = conv.reduce((s, x) => s + x.value, 0);
    return conv.map(({ a, value }, i) => ({
      id: a.id, name: a.name, type: a.type, value,
      pct: totalVal > 0 ? Math.round((value / totalVal) * 100) : 0,
      color: COLORS[i % COLORS.length],
    }));
  }, [items, rates, home]);

  const total = data.reduce((s, x) => s + x.value, 0);
  let cum = 0;
  const segs = data.map((d) => {
    const len = (d.value / (total || 1)) * C;
    const seg = { ...d, len, dash: `${len} ${C - len}`, offset: -cum };
    cum += len;
    return seg;
  });

  const empty = items.length === 0 || total === 0;

  return (
    <div data-testid={empty ? "accounts-donut-empty" : "accounts-donut-card"} className="neu p-4 sm:p-5 h-full">
      <div className="flex items-start gap-4">
        <div className="flex-1 min-w-0 text-start space-y-1">
          <p className="font-display text-lg font-bold neu-accent-blue leading-tight">{t("wealthDistribution")}</p>
          {empty ? (
            <p className="text-xs sm:text-sm text-neu-muted leading-snug">{t("noAccounts")}</p>
          ) : (
            <>
              <p className="text-xs sm:text-sm text-neu-muted leading-snug">{lang === "ar" ? "توزيع أموالك عبر الحسابات" : "Across your accounts"}</p>
              <ul className="pt-1 space-y-1" data-testid="accounts-donut-legend">
                {segs.map((d) => {
                  const Ic = ICON[d.type] || Landmark;
                  return (
                    <li key={d.id} className="flex items-center gap-2 text-xs">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />
                      <Ic className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="truncate font-semibold text-neu-ink/80">{d.name}</span>
                      <span className="tabular-nums text-neu-muted shrink-0">{d.pct}%</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div className="relative w-[120px] aspect-square shrink-0 rounded-full flex items-center justify-center self-center"
             style={{ background: "#eceef1", boxShadow: "inset 6px 6px 12px #c8cad0, inset -6px -6px 12px #ffffff" }}>
          <svg viewBox="0 0 220 220" className="w-[92%] h-[92%] -rotate-90">
            <defs>
              <linearGradient id="donutTrack" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#b8babf" /><stop offset="55%" stopColor="#d8dade" /><stop offset="100%" stopColor="#ffffff" />
              </linearGradient>
              <filter id="donutShadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="2" dy="3" stdDeviation="2.5" floodColor="#000000" floodOpacity="0.22" />
                <feDropShadow dx="-1" dy="-1" stdDeviation="1.5" floodColor="#ffffff" floodOpacity="0.9" />
              </filter>
            </defs>
            <circle cx="110" cy="110" r={R} fill="none" stroke="url(#donutTrack)" strokeWidth={STROKE} />
            {segs.map((s) => (
              <circle key={s.id} cx="110" cy="110" r={R} fill="none" stroke={s.color} strokeWidth={STROKE}
                strokeDasharray={s.dash} strokeDashoffset={s.offset} filter="url(#donutShadow)"
                style={{ transition: "stroke-dashoffset 700ms ease" }} />
            ))}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="font-display font-extrabold tabular-nums leading-none neu-accent-blue"
               style={{ fontSize: "0.95rem", textShadow: "1px 1px 2px rgba(255,255,255,0.95), -1px -1px 2px rgba(0,0,0,0.1)" }}>
              {empty ? "—" : Math.round(total)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
